import { hasAccessToken, setAccessToken } from '#/shared/api/http-client'

const REFRESH_TOKEN_KEY = 'thecargo.admin.refresh'

export interface AdminUser {
  id: string
  email: string
  first_name?: string | null
  last_name?: string | null
}

function safely<T>(operation: () => T): T | null {
  try {
    return operation()
  } catch {
    return null
  }
}

export function getRefreshToken(): string | null {
  return (
    safely(() => sessionStorage.getItem(REFRESH_TOKEN_KEY)) ??
    safely(() => localStorage.getItem(REFRESH_TOKEN_KEY))
  )
}

export function isRemembered(): boolean {
  return safely(() => localStorage.getItem(REFRESH_TOKEN_KEY)) !== null
}

export function storeSession(
  accessToken: string,
  refreshToken: string,
  options: { remember: boolean },
): void {
  setAccessToken(accessToken)

  clearStoredToken()

  const storage = options.remember ? localStorage : sessionStorage
  safely(() => storage.setItem(REFRESH_TOKEN_KEY, refreshToken))
}

export function rotateStoredToken(accessToken: string, refreshToken: string): void {
  storeSession(accessToken, refreshToken, { remember: isRemembered() })
}

function clearStoredToken(): void {
  safely(() => localStorage.removeItem(REFRESH_TOKEN_KEY))
  safely(() => sessionStorage.removeItem(REFRESH_TOKEN_KEY))
}

export function clearSession(): void {
  setAccessToken(null)
  clearStoredToken()
}

export function isAuthenticated(): boolean {
  return hasAccessToken()
}

export function displayName(user: AdminUser): string {
  const parts = [user.first_name, user.last_name].filter(Boolean)
  return parts.length > 0 ? parts.join(' ') : user.email
}

const PENDING_SETUP_KEY = 'thecargo.admin.passkey-setup'

/**
 * A sign-in that is waiting for the admin to add a passkey.
 *
 * The password (or a recovery code) has been accepted, but the console only
 * opens once a passkey exists. The setup token proves that first step for a
 * few minutes; it lives in this tab only and is dropped as soon as it is used.
 */
export interface PendingSetup {
  setupToken: string
  email: string
  reason: 'required' | 'recovery'
  remember: boolean
  expiresAt: number
}

export function storePendingSetup(setup: PendingSetup): void {
  safely(() => sessionStorage.setItem(PENDING_SETUP_KEY, JSON.stringify(setup)))
}

export function readPendingSetup(): PendingSetup | null {
  const raw = safely(() => sessionStorage.getItem(PENDING_SETUP_KEY))
  if (!raw) return null
  const setup = safely(() => JSON.parse(raw) as PendingSetup)
  return setup && setup.expiresAt > Date.now() ? setup : null
}

export function clearPendingSetup(): void {
  safely(() => sessionStorage.removeItem(PENDING_SETUP_KEY))
}
